import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

/*
  The locale switch, as a two-option segmented control.

  It used to be one 42x15px button reading "MN · EN", with no accessible name,
  no pressed state, and the active locale signalled by `text-text-1` against
  `text-text-3` — colour alone (WCAG 1.4.1), under both the 24x24 minimum and
  the 44x44 target (WCAG 2.5.8), and shaped like static text rather than a
  control.

  Each locale is now its own button carrying `aria-pressed`, so the state is
  programmatic rather than inferred from a shade, and the active option is
  marked three ways that survive greyscale: a filled pill, a heavier weight, and
  the pressed state itself. The pair sits in a `role="group"` named "Language",
  which is where the accessible name comes from — "MN" and "EN" are codes, not
  words, and issue 02 owns any wording beyond them.

  Each option is 40x44 and the control is ~98x46. 44 tall rather than 40 would
  clear WCAG 2.5.8's usability target on both axes, but it also makes the toggle
  the tallest thing in a 56px header that otherwise tops out at the 36px nav
  buttons; 40 stays well clear of the 24x24 minimum and reads as a peer of the
  controls beside it.

  Call sites: the landing nav (desktop bar and mobile drawer), the About and FAQ
  headers, and the /y profile settings row. The first four absorb the new
  footprint in existing header gaps, the last in a row that was already 48px.
*/
const LOCALES = [
  { code: "mn", label: "MN" },
  { code: "en", label: "EN" },
] as const;

export default function LangToggle({ className }: { className?: string }) {
  const { i18n, t } = useTranslation();
  /* i18n.language can be a region tag ("en-GB"); match on the base subtag. */
  const active = i18n.language?.split("-")[0] === "en" ? "en" : "mn";

  const select = (next: string) => {
    if (next === active) return;
    i18n.changeLanguage(next);
    localStorage.setItem("astring-lang", next);
  };

  return (
    <div
      role="group"
      aria-label={t("common.language")}
      className={cn(
        "inline-flex shrink-0 items-center gap-0.5 rounded-full border border-hairline p-0.5",
        className
      )}
    >
      {LOCALES.map(({ code, label }) => (
        <button
          key={code}
          type="button"
          onClick={() => select(code)}
          aria-pressed={active === code}
          className={cn(
            "inline-flex h-10 min-w-11 items-center justify-center rounded-full px-2 text-meta transition-colors",
            active === code
              ? "bg-surface-strong font-semibold text-text-1"
              : "font-normal text-text-3 hover:text-text-2"
          )}
        >
          {label}
        </button>
      ))}
    </div>
  );
}
